'use client';

import { useTranslations } from 'next-intl';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

function GiftIllustration() {
  return (
    <svg aria-hidden="true" viewBox="0 0 100 76" className="mx-auto h-[90px] w-[120px] sm:h-[99px] sm:w-[132px]">
      <defs>
        <linearGradient id="daily-gift-box" x2="0" y2="1">
          <stop stopColor="#ffca64" /><stop offset="1" stopColor="#f59e0b" />
        </linearGradient>
        <linearGradient id="daily-gift-coin" x2="0" y2="1">
          <stop stopColor="#ffe68b" /><stop offset="1" stopColor="#f3b93d" />
        </linearGradient>
      </defs>
      <path d="m12 10 3 3-3 3-3-3Zm81 43 3 3-3 3-3-3Z" fill="none" stroke="#f8c15c" strokeWidth="1.2" />
      <path d="m15 47 2 2-2 2-2-2Z" fill="none" stroke="#ffbd32" />
      <path d="m90 59 3 3-3 3-3-3Z" fill="none" stroke="#e89024" />
      <rect x="27" y="32" width="39" height="28" rx="4" fill="url(#daily-gift-box)" />
      <rect x="23" y="27" width="47" height="9" rx="3" fill="#f8bc50" />
      <path d="M42 29h9v31h-9z" fill="#fff1e5" />
      <path d="M46 27c-7-1-14-5-13-9 2-4 11 2 13 9Zm0 0c6-2 13-5 12-9-2-4-10 2-12 9Z" fill="#fff3e9" />
      <ellipse cx="71" cy="39" rx="20" ry="21" fill="#bf7d28" />
      <ellipse cx="69" cy="37" rx="20" ry="21" fill="url(#daily-gift-coin)" stroke="#fff0b5" strokeWidth="2" />
      <ellipse cx="69" cy="37" rx="15.5" ry="16.5" fill="none" stroke="#dc9b32" strokeWidth="1.3" />
      <path d="M72.5 30.5c-1-2-7-2.5-7.5 1.5-.6 4.5 8 3 8 7.5 0 4-6.5 4.3-8.5 1.5M69 27v20" fill="none" stroke="#7a4b13" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

export default function DailyCreditRestoreDialog({ open, credits, restoring, error, onRestore, onDismiss }) {
  const t = useTranslations('DailyCreditRestore');
  const quota = credits?.dailyCreditWindow?.quota || 0;
  const remaining = credits?.freeDailyCredits || 0;
  const alreadyUsed = remaining < quota;

  return (
    <Dialog open={open} onOpenChange={value => { if (!value) onDismiss(); }}>
      <DialogContent
        data-daily-credit-restore="true"
        className="daily-credit-restore max-h-[90dvh] w-[calc(100vw-2rem)] max-w-[420px] gap-0 overflow-y-auto rounded-[27px] p-0 sm:rounded-[27px]"
        overlayClassName="bg-black/70 backdrop-blur-sm"
      >
        <div className="daily-credit-restore-hero px-6 pb-[21px] pt-8 text-center sm:pt-9">
          <GiftIllustration />
          <DialogTitle className="mx-auto mt-6 max-w-[345px] text-[26px] font-bold leading-8 tracking-tight sm:text-[30px] sm:leading-9">
            {t.rich(alreadyUsed ? 'restoredTitle' : 'title', {
              count: quota,
              amount: chunks => <span className="text-primary">{chunks}</span>,
              line: chunks => <span className="block">{chunks}</span>,
            })}
          </DialogTitle>
        </div>
        <div className="px-6 pb-[18px] pt-[21px]">
          <div className="flex min-h-[69px] items-center gap-3 rounded-[18px] border border-border bg-muted/40 px-[15px] py-3">
            <span aria-hidden="true" className="daily-credit-restore-mini-coin flex size-[30px] shrink-0 items-center justify-center rounded-full text-[16px] font-bold">$</span>
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-medium text-foreground sm:text-[15px]">{t('todaysCredits')}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground sm:text-[12px]">{alreadyUsed ? t('remaining', { count: remaining }) : t('available')}</p>
            </div>
            <span className="text-[18px] font-semibold text-amber-400">+{quota}</span>
          </div>
          {error && <p role="alert" className="mt-2 text-center text-xs text-destructive">{t('error')}</p>}
          <Button
            className="daily-credit-restore-action mt-[18px] h-[56px] w-full rounded-[15px] text-[16px] font-semibold transition-transform hover:scale-[1.02]"
            disabled={restoring}
            onClick={onRestore}
          >
            {restoring ? t('restoring') : alreadyUsed ? t('continue') : t('restore', { count: quota })}
          </Button>
          <DialogDescription className="mt-3 text-center text-[11px] leading-[18px] sm:text-[12px]">{t('resetHint')}</DialogDescription>
        </div>
      </DialogContent>
    </Dialog>
  );
}
